import { ref, computed, watch } from 'vue'
import { defineStore } from 'pinia'
import { useTheme } from 'vuetify'

export const useThemeStore = defineStore('core-theme', () => {
  const themeMode = ref<'light' | 'dark' | 'system'>('system')

  let vuetifyTheme: ReturnType<typeof useTheme> | null = null

  try {
    vuetifyTheme = useTheme()
  } catch {
    // Vuetify not available yet (during SSR or before app mount)
  }

  if (typeof window !== 'undefined') {
    const storedTheme = localStorage.getItem('app-theme-mode')
    if (storedTheme && ['light', 'dark', 'system'].includes(storedTheme)) {
      themeMode.value = storedTheme as 'light' | 'dark' | 'system'
    }
  }

  const systemPrefersDark = ref(false)

  if (typeof window !== 'undefined') {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    systemPrefersDark.value = mediaQuery.matches
    mediaQuery.addEventListener('change', (e) => {
      systemPrefersDark.value = e.matches
      if (themeMode.value === 'system') {
        applyTheme()
      }
    })
  }

  const effectiveTheme = computed(() => {
    if (themeMode.value === 'system') {
      return systemPrefersDark.value ? 'dark' : 'light'
    }
    return themeMode.value
  })

  function applyTheme() {
    if (vuetifyTheme) {
      vuetifyTheme.change(effectiveTheme.value)
    }
  }

  function setTheme(mode: 'light' | 'dark' | 'system') {
    themeMode.value = mode
    if (typeof window !== 'undefined') {
      localStorage.setItem('app-theme-mode', mode)
    }
    applyTheme()
  }

  function toggleTheme() {
    const modes: Array<'light' | 'dark' | 'system'> = ['light', 'dark', 'system']
    const currentIndex = modes.indexOf(themeMode.value)
    const nextIndex = (currentIndex + 1) % modes.length
    setTheme(modes[nextIndex])
  }

  function initTheme() {
    try {
      vuetifyTheme = useTheme()
      applyTheme()
    } catch {
      // Vuetify not available
    }
  }

  // Apply theme on initial load if vuetify is available
  if (vuetifyTheme) {
    applyTheme()
  }

  return {
    themeMode,
    effectiveTheme,
    systemPrefersDark,
    setTheme,
    toggleTheme,
    initTheme,
  }
})
